package dev.lingvo.shared.screenshots.render

import dev.lingvo.shared.textDirection
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.doc.TextTransform
import dev.lingvo.shared.screenshots.geom.Rect
import dev.lingvo.shared.screenshots.text.DefaultFontResolver
import dev.lingvo.shared.screenshots.text.FitRequest
import dev.lingvo.shared.screenshots.text.Scripts
import dev.lingvo.shared.screenshots.text.FontStack
import dev.lingvo.shared.screenshots.text.cssFont

internal fun paintText(layer: TextLayer, rect: Rect, session: RenderSession) {
    val inputs = session.inputs
    val content = layer.content
    val index = session.slot(if (content is TextContent.SlotCaption) content.slot else layer.base.roleSlot)
    val text = if (content is TextContent.Literal) content.text else inputs.captions(content, index)
    if (text == null) {
        session.missing += MissingInput.Caption(layer.base.id, content)
        return
    }
    val style = layer.style
    val script = inputs.scripts.scriptFor(inputs.locale, text)
    val emoji = Scripts.hasEmoji(text)
    // The legacy interface has no style arguments; the production resolver exposes a weight-aware overload.
    val stack = if (inputs.fonts === DefaultFontResolver) {
        DefaultFontResolver.stack(style.role, style.family, session.design.brand, inputs.templateFamilies[style.role],
            script, emoji, style.weight, style.italic)
    } else {
        inputs.fonts.stack(style.role, style.family, session.design.brand, inputs.templateFamilies[style.role], script, emoji)
    }
    val request = FitRequest(text, inputs.locale, rect.w, rect.h, stack, style.weight, style.italic, layer.fit,
        style.lineHeight, style.letterSpacingEm, style.align, style.vAlign, style.transform == TextTransform.UPPERCASE, style.quoted)
    val fit = session.cache.fit(request, session.fitter, inputs.measurer, inputs.segmenter)
    val layout = fit.layout
    val font = resolvedFont(stack, layout.size, style.weight, style.italic)
    val paint = resolvePaint(style.color, rect, session.design.palette, index)
    layout.lines.forEach { line ->
        session.surface.fillText(line.text, line.anchorX, line.baselineY, font, paint, line.canvasAlign,
            textDirection(inputs.locale), layout.letterSpacingPx)
    }
    session.texts += TextReport(layer.base.id, content, text, layout.lines.joinToString("\n") { it.text }, fit, font,
        primaryCaption = layer.base.id == session.primaryCaptionId)
}

private fun resolvedFont(stack: FontStack, size: Double, weight: Int, italic: Boolean): ResolvedFont =
    ResolvedFont(cssFont(stack, size, weight, italic), size, stack.families + stack.generic, weight, italic)
